import {useCallback, useContext, useEffect, useState, useRef} from "react";
import {useTranslation as reactUseTranslation} from "react-i18next";
import cloneDeep from "lodash-es/cloneDeep";
import groupBy from "lodash-es/groupBy";
import uniqWith from "lodash-es/uniqWith";
import i18n from "Localization";
import Configuration from "../Configuration";
import {StateContext} from "../context/StateContext";
import Services, {subscriptionKeys} from "../services/Services";
import {
  getReferActions,
  getRespondActions,
  respondAction
} from "../services/pivotActions";
import {getUserInfo} from "./auth";
import DispositionCache from "../services/disposition/DispositionCache";
import Subscriptions from "../Subscriptions";

export const useDebounce = (value, delay = 300) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [delay, value]);

  return debouncedValue;
};

export const useMounted = (mount, unmount) => {
  const isMounted = useRef(false);

  /* eslint-disable */
  useEffect(() => {
    if (!isMounted.current && mount) {
      mount();
    }

    isMounted.current = true;

    return () => {
      isMounted.current = false;

      if (!isMounted.current && unmount) {
        unmount();
      }
    };
  }, []);
  /* eslint-enable */

  return isMounted;
};

export const useTranslation = (namespace = null, options = {}) => {
  return reactUseTranslation(namespace, {i18n, ...options});
};

/* eslint-disable react-hooks/rules-of-hooks */
export const useServicesReadyState = mounted => {
  const [apiHost, setAPIHost] = useState(!!Services.apiHost),
    [credentials, setCredentials] = useState(!!Services.credentials);

  const updateHost = useCallback(
    host => {
      if (mounted && !mounted.current) {
        return;
      }

      if (!!host === apiHost) {
        return;
      }

      setAPIHost(!!host);
    },
    [mounted, apiHost, setAPIHost]
  );

  const updateCreds = useCallback(
    creds => {
      if (mounted && !mounted.current) {
        return;
      }

      if (!!creds === credentials) {
        return;
      }

      setCredentials(!!creds);
    },
    [mounted, credentials, setCredentials]
  );

  useEffect(() => {
    const {apiHostSet, credentialsSet} = subscriptionKeys,
      a = Subscriptions.subscribe(apiHostSet, updateHost),
      b = Subscriptions.subscribe(credentialsSet, updateCreds);

    return () => {
      Subscriptions.unsubscribe(a);
      Subscriptions.unsubscribe(b);
    };
  }, [updateHost, updateCreds, mounted]);

  return apiHost && credentials;
};

/*
  Returns the disposition.

  `full` - returns disposition and verdicts in an object
*/
export const useDisposition = (
  type,
  value,
  defaultDisposition,
  full = false
) => {
  const [dispositionData, setDisposition] = useState({
    disposition: defaultDisposition || 5
  });

  useEffect(() => {
    if (!type || !value) {
      return;
    }

    const updateDisposition = updated => {
      setDisposition(updated);
    };

    // No disposition, queue it up
    const observable = {type, value},
      key = DispositionCache.observableKey({type, value}),
      cached = DispositionCache.addToQueue(observable),
      fromCache = cached[key];

    if (fromCache) {
      updateDisposition(fromCache);
    }

    const subscriptionKey = `${DispositionCache.updateKey}-${key}`;

    const subId = Subscriptions.subscribe(subscriptionKey, updateDisposition);

    return () => {
      Subscriptions.unsubscribe(subId);
    };
  }, [defaultDisposition, type, value]);

  // If the defaultDisposition is set, and it changes, update.
  useEffect(() => {
    if (
      defaultDisposition &&
      defaultDisposition !== dispositionData.disposition
    ) {
      const clone = cloneDeep(dispositionData);
      clone.disposition = defaultDisposition;

      setDisposition(clone);
    }
  }, [defaultDisposition, dispositionData, setDisposition]);

  return full ? dispositionData : dispositionData.disposition;
};

export const useDispositionData = (type, value, defaultDisposition) => {
  return useDisposition(type, value, defaultDisposition, true);
};

/*
  Returns dispositions array for given array of observables
*/
export const useDispositions = (
  observables = [],
  immediateReconcile = false,
  sort = false,
  options = {}
) => {
  const [dispositions, setDispositions] = useState(observables),
    {waitForAll = false} = options;

  const updateObservables = useCallback(
    updated => {
      const clone = cloneDeep(observables),
        cached = DispositionCache.getDispositionsFromCache(clone);

      DispositionCache.mergeObservables(clone, cached);
      DispositionCache.mergeObservables(clone, updated);

      if (sort) {
        if (typeof sort === "function") {
          clone.sort(sort);
        } else {
          clone.sort((a, b) => {
            if (a.disposition === b.disposition) {
              return 0;
            }

            return (a.disposition || 5) < (b.disposition || 5) ? 1 : -1;
          });
        }
      }

      const uniq = uniqWith(clone, (a, b) => {
        return a.type === b.type && a.value === b.value;
      });
      setDispositions(uniq);
    },
    [observables, sort]
  );

  // Subscribe to observable cache updates
  useEffect(() => {
    if (!observables) {
      setDispositions([]);
      return;
    }

    // Do an initial call to grab anything already cached and make the calls for anything else
    observables.forEach(observable => {
      DispositionCache.addToQueue(observable);
    });

    updateObservables([]);

    if (immediateReconcile) {
      DispositionCache.reconcileQueue();
    }

    const subId = Subscriptions.subscribe(
      DispositionCache.updateKey,
      updateObservables
    );

    return () => {
      Subscriptions.unsubscribe(subId);
    };
  }, [observables, immediateReconcile, sort, updateObservables]);

  if (
    waitForAll &&
    dispositions.some(
      d => d.status === DispositionCache.dispositionStatus.PENDING
    )
  ) {
    return null;
  }

  return dispositions;
};

export const usePrevious = value => {
  const ref = useRef();

  useEffect(() => {
    ref.current = value;
  }, [value]);
  return ref.current;
};

const moduleIdentifier = "module_instance_id";

const getFirstOfGroup = (
  key,
  referGroups,
  respondGroups,
  moduleErrorGroups
) => {
  const referGroup = referGroups[key],
    respondGroup = respondGroups[key],
    moduleErrorGroup = moduleErrorGroups[key];

  return (
    (referGroup && referGroup[0]) ||
    (respondGroup && respondGroup[0]) ||
    (moduleErrorGroup && moduleErrorGroup[0])
  );
};

export const useRemoteActions = (
  type,
  value,
  open,
  _disableRefer = false,
  _disableRespond = false,
  filterRefer,
  filterRespond
) => {
  const globalOptions = Configuration.options.pivot,
    {
      disableRefer: _globalDisableRefer,
      disableRespond: _globalDisableRespond
    } = globalOptions;

  // Local disable props override global, true or false
  let disableRefer = _globalDisableRefer,
    disableRespond = _globalDisableRespond;

  if (typeof _disableRefer !== "undefined") {
    disableRefer = _disableRefer;
  }

  if (typeof _disableRespond !== "undefined") {
    disableRespond = _disableRespond;
  }

  const [referActions, setReferActions] = useState([]),
    [referLoading, setReferLoading] = useState(!disableRefer),
    [referError, setReferError] = useState(),
    [respondActions, setRespondActions] = useState([]),
    [respondLoading, setRespondLoading] = useState(!disableRespond),
    [respondError, setRespondError] = useState(),
    [moduleErrors, setModuleErrors] = useState([]),
    _mounted = useMounted();

  // Reset actions on pivot close
  useEffect(() => {
    if (!open) {
      setReferActions([]);
      setRespondActions([]);
      setModuleErrors([]);
    }
  }, [open, _disableRefer, _disableRespond]);

  // Fetch RESPOND
  const fetchRespond = useCallback(
    (silent, callback) => {
      if (!open || disableRespond) {
        return;
      }

      if (!silent) {
        setRespondLoading(true);
        setRespondError(null);
      }

      const {promise, cancel} = getRespondActions({type, value});

      promise
        .then(({data}) => {
          if (!_mounted.current) {
            return;
          }

          const actions = data.data,
            errors = data.errors || [],
            filterHook = Configuration.hooks.pivot.filterRespond;

          // First filter by global hook
          let filtered = actions || [];
          if (filterHook) {
            const filt = filterHook({type, value}, actions);
            filtered = Array.isArray(filt) ? filt : [];
          }

          // Then filter by local function
          if (filterRespond) {
            const filt = filterRespond({type, value}, filtered);
            filtered = Array.isArray(filt) ? filt : [];
          }

          filtered.forEach(a => {
            a.type = "respond";
          });

          setRespondActions(filtered);
          setRespondLoading(false);

          if (Array.isArray(errors)) {
            setModuleErrors(currModuleErrors => [
              ...currModuleErrors,
              ...errors
            ]);
          }

          callback && callback();
        })
        .catch(e => {
          if (e.name === "RequestCancelled") {
            return;
          }

          setRespondError(e);
          setRespondLoading(false);
        });

      return cancel;
    },
    [type, value, open, disableRespond, filterRespond, _mounted]
  );

  // Fetch REFER
  useEffect(() => {
    if (!open || disableRefer) {
      return;
    }

    setReferLoading(true);
    setReferError(null);

    const {promise, cancel} = getReferActions({type, value});

    promise
      .then(({data}) => {
        const actions = data.data,
          errors = data.errors || [],
          filterHook = Configuration.hooks.pivot.filterRefer;

        // First filter by global hook
        let filtered = actions || [];
        if (filterHook) {
          const filt = filterHook({type, value}, actions);
          filtered = Array.isArray(filt) ? filt : [];
        }

        // Then filter by local function
        if (filterRefer) {
          const filt = filterRefer({type, value}, filtered);
          filtered = Array.isArray(filt) ? filt : [];
        }

        filtered.forEach(a => {
          a.type = "refer";
        });

        setReferActions(filtered);
        setReferLoading(false);

        if (Array.isArray(errors)) {
          setModuleErrors(currModuleErrors => [...currModuleErrors, ...errors]);
        }
      })
      .catch(e => {
        if (e.name === "RequestCancelled") {
          return;
        }

        setReferError(e);
        setReferLoading(false);
      });

    return cancel;
  }, [type, value, open, disableRefer, filterRefer]);

  useEffect(() => {
    if (!open || disableRespond) {
      return;
    }

    // Abstracted since it gets called after a respond action runs
    return fetchRespond();
  }, [fetchRespond, open, disableRespond]);

  let groups = [];

  const firstItem = referActions[0] || respondActions[0] || moduleErrors[0];

  if (firstItem) {
    // https://github.com/threatgrid/iroh-engine/issues/308

    let referGroups, respondGroups, moduleErrorGroups;
    if (!firstItem[moduleIdentifier]) {
      referGroups = groupBy(referActions, "module");
      respondGroups = groupBy(respondActions, "module");
      moduleErrorGroups = groupBy(moduleErrors, "module");
    } else {
      referGroups = groupBy(referActions, moduleIdentifier);
      respondGroups = groupBy(respondActions, moduleIdentifier);
      moduleErrorGroups = groupBy(moduleErrors, moduleIdentifier);
    }

    const modules = [
      ...Object.keys(referGroups),
      ...Object.keys(respondGroups),
      ...Object.keys(moduleErrorGroups)
    ];

    const uniqModuleIds = [...new Set(modules)];

    groups = uniqModuleIds
      .sort((a, b) => {
        const ma = getFirstOfGroup(
            a,
            referGroups,
            respondGroups,
            moduleErrorGroups
          ),
          mb = getFirstOfGroup(
            b,
            referGroups,
            respondGroups,
            moduleErrorGroups
          );

        return ma.module.localeCompare(mb.module);
      })
      .map(moduleId => {
        const firstOfGroup = getFirstOfGroup(
          moduleId,
          referGroups,
          respondGroups,
          moduleErrorGroups
        );

        let title;

        if (firstOfGroup) {
          title = firstOfGroup.module;
        }

        return {
          moduleId,
          title,
          refer: referGroups[moduleId],
          respond: respondGroups[moduleId],
          moduleErrors: moduleErrorGroups[moduleId]
        };
      });
  }

  return {
    referLoading,
    respondLoading,
    referError,
    respondError,
    referActions,
    respondActions,
    fetchRespond,
    respondAction,
    moduleErrors,
    groups
  };
};

export const useWhoami = () => {
  const {whoami} = useContext(StateContext),
    user = whoami?.user || {},
    org = whoami?.org || {};

  return {user, org};
};

export const useUser = userId => {
  const {t} = useTranslation("misc"),
    [userInfo, setUserInfo] = useState({});

  useEffect(() => {
    setUserInfo({});

    const request = getUserInfo(userId);

    if (!request) {
      return;
    }

    const {promise, cancel} = request;

    promise
      .then(({data}) => {
        const {"enabled?": enabled} = data;

        if (enabled) {
          setUserInfo(data);
        } else {
          const censored = `[${t("misc.private")}]`;

          setUserInfo({"user-name": censored, "user-nick": censored});
        }
      })
      .catch(() => {});

    return cancel;
  }, [userId, t, setUserInfo]);

  return userInfo;
};

export const useMemoCompare = (next, compare) => {
  const previousRef = useRef(),
    previous = previousRef.current,
    isEqual = compare(previous, next);

  useEffect(() => {
    if (!isEqual) {
      previousRef.current = next;
    }
  });

  return isEqual ? previous : next;
};

export const useSubscription = (topic, fn, deps = []) => {
  if (!Array.isArray(deps)) {
    console.warn(
      "Custom React Hook useSubscription was passed a dependency list that is not an array literal. This means we can't statically verify whether you've passed the correct dependencies."
    );
  }
  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    const subId = Subscriptions.subscribe(topic, fn);
    return () => Subscriptions.unsubscribe(subId);
  }, deps);
};

export const useSubscriptions = (subscriptions = [], deps = []) => {
  useEffect(() => {
    const cleanUpIds = [];

    subscriptions.forEach(({topic, fn}) => {
      const subId = Subscriptions.subscribe(topic, fn);
      cleanUpIds.push(subId);
    });

    return () => {
      cleanUpIds.forEach(subId => Subscriptions.unsubscribe(subId));
    };
  }, deps);
};
